/**
 * Pure Generation & Impact screening-evidence resolver.
 *
 * This module does not fetch, persist, or mutate source records. It projects
 * already-validated document facts, exact SED/site facts, and unit-structured
 * DAM benchmarks onto a planning draft. Existing user/project/vendor values
 * and applied equipment/project-duty authority always win.
 */
import type {
  AssumptionSource,
  GenerationAssumptionEvidenceRef,
  GenerationImpactScenarioInput,
  GenerationScreeningDocumentFact,
  GenerationScreeningEvidenceField,
  GenerationTechnology,
} from '../../types/generationImpact';
import {
  GENERATION_SCREENING_EXTRACTOR_VERSION,
} from '../../types/generationImpact';
import type { SiteEntity } from '../../types/site';
import type { SiteDocument } from '../../types/siteRag';
import type { SiteEvidenceDocument } from '../../types/siteEvidence';
import { isIntentFieldExplicitlyUnknown } from '../intent/explicitUnknown';
import type {
  DatacrateModel,
  NormalizedBenchmark,
} from '../../types/datacrateModel';
import {
  suggestAmbientFactorFromSed,
  suggestNearestReceptorDistance,
} from './siteEvidenceSuggestions';
import { planningAmbientFactor } from './planningCatalog';
import { utilityPipelineProjectionIsCurrent } from '../sed/utilityPipelineDocumentProjection';
import {
  isScreeningWaterFactField,
  screeningWaterFactRoleIsBound,
  screeningWaterRoleTokens,
} from './screeningWaterFactRoles';

export const GALLONS_PER_ACRE_FOOT = 325_851;
export const DAYS_PER_YEAR = 365;
export const GALLONS_PER_MGD = 1_000_000;
export const GALLONS_PER_LITRE = 0.264_172_052;
export const GALLONS_PER_MWH_IT_PER_LITRE_PER_KWH_IT = 264.172_052;

export const GENERATION_SCREENING_PREFILL_FIELDS = [
  'units.ambientFactor',
  'availableGasCapacityDthPerDay',
  'availableWaterConsumptionGalPerDay',
  'ccsExportCapacityTonnesPerYear',
  'water.generationConsumptionGalPerMWhGross',
  'water.dataCenterConsumptionGalPerMWhFacility',
  'footprint.plantAcresPerGrossMW',
  'receptors.nearest.distanceM',
  'lighting.fixtureCount',
  'lighting.wattsPerFixture',
  'lighting.lumensPerFixture',
  'lighting.fullyShielded',
  'lighting.bugRated',
  'lighting.motionOrCurfewControls',
  'lighting.iesPhotometricAvailable',
] as const;

export type GenerationScreeningPrefillField =
  typeof GENERATION_SCREENING_PREFILL_FIELDS[number];

export type GenerationScreeningAutomaticSource =
  | 'document'
  | 'site-evidence'
  | 'dam';

type PrefillValue = number | boolean;

export interface GenerationScreeningPrefillCandidate {
  field: GenerationScreeningPrefillField;
  value: PrefillValue;
  source: GenerationScreeningAutomaticSource;
  label: string;
  citation: string;
  confidence: 'low' | 'medium' | 'high';
  evidenceRefs: GenerationAssumptionEvidenceRef[];
  /** Stable identity used for deterministic ordering and conflict reporting. */
  sourceKey: string;
}

export interface GenerationScreeningPrefillDecision {
  field: GenerationScreeningPrefillField;
  candidate: GenerationScreeningPrefillCandidate;
  previousValue: PrefillValue | undefined;
  applied: boolean;
  reason:
    | 'applied'
    | 'already-current'
    | 'protected-assumption'
    | 'protected-field'
    | 'not-refreshable'
    | 'equipment-authority'
    | 'project-duty-authority';
}

export interface GenerationScreeningPrefillConflict {
  field: GenerationScreeningPrefillField;
  source: GenerationScreeningAutomaticSource;
  message: string;
  candidates: GenerationScreeningPrefillCandidate[];
}

export interface GenerationScreeningPrefillRejection {
  source: GenerationScreeningAutomaticSource;
  sourceId: string;
  field?: GenerationScreeningPrefillField;
  code:
    | 'document-excluded'
    | 'hash-missing'
    | 'hash-stale'
    | 'extractor-stale'
    | 'extraction-incomplete'
    | 'fact-not-eligible'
    | 'invalid-value'
    | 'unit-mismatch'
    | 'technology-mismatch'
    | 'cooling-mismatch'
    | 'load-basis-mismatch'
    | 'basis-ambiguous'
    | 'lineage-invalid'
    | 'sed-section-ineligible'
    | 'not-firm-site-deliverable'
    | 'unsupported-pipeline-unit'
    | 'benchmark-invalid'
    | 'benchmark-citation-missing'
    | 'benchmark-not-applicable'
    | 'pue-required'
    | 'defective-feasibility-slot-quarantined';
  message: string;
}

export interface ResolveGenerationScreeningPrefillArgs {
  input: GenerationImpactScenarioInput;
  site: SiteEntity;
  sed?: SiteEvidenceDocument | null;
  documents?: readonly SiteDocument[];
  dams?: readonly DatacrateModel[];
  /**
   * When supplied during a late refresh, only these auto-owned fields may be
   * reconsidered. Omit for a newly built/reset planning baseline.
   */
  refreshableFields?: ReadonlySet<GenerationScreeningPrefillField>;
  /** Explicit ownership override used by the workbench for manual clears. */
  protectedFields?: ReadonlySet<GenerationScreeningPrefillField>;
}

export interface GenerationScreeningPrefillResolution {
  input: GenerationImpactScenarioInput;
  decisions: GenerationScreeningPrefillDecision[];
  conflicts: GenerationScreeningPrefillConflict[];
  rejections: GenerationScreeningPrefillRejection[];
}

const SOURCE_RANK: Record<GenerationScreeningAutomaticSource, number> = {
  document: 3,
  'site-evidence': 2,
  dam: 1,
};

const PROTECTED_ASSUMPTION_SOURCES = new Set<AssumptionSource>([
  'user',
  'project-intent',
  'vendor',
]);

const AUTO_ASSUMPTION_SOURCES = new Set<AssumptionSource>([
  'document',
  'site-evidence',
  'dam',
  'planning-default',
]);

const ASSUMPTION_FIELD_ALIASES:
Readonly<Record<GenerationScreeningPrefillField, readonly string[]>> = {
  'units.ambientFactor': ['units.ambientFactor', 'ambientDerate', 'ambientFactor'],
  availableGasCapacityDthPerDay: ['availableGasCapacityDthPerDay'],
  availableWaterConsumptionGalPerDay: ['availableWaterConsumptionGalPerDay'],
  ccsExportCapacityTonnesPerYear: ['ccsExportCapacityTonnesPerYear'],
  'water.generationConsumptionGalPerMWhGross': [
    'water.generationConsumptionGalPerMWhGross',
    'generationConsumptionGalPerMWhGross',
  ],
  'water.dataCenterConsumptionGalPerMWhFacility': [
    'water.dataCenterConsumptionGalPerMWhFacility',
    'dataCenterConsumptionGalPerMWhFacility',
  ],
  'footprint.plantAcresPerGrossMW': [
    'footprint.plantAcresPerGrossMW',
    'plantAcresPerGrossMW',
  ],
  'receptors.nearest.distanceM': [
    'receptors.nearest.distanceM',
    'receptors.distanceM',
    'receptorDistance',
    'nearestReceptorDistanceM',
  ],
  'lighting.fixtureCount': ['lighting.fixtureCount'],
  'lighting.wattsPerFixture': ['lighting.wattsPerFixture'],
  'lighting.lumensPerFixture': ['lighting.lumensPerFixture'],
  'lighting.fullyShielded': ['lighting.fullyShielded'],
  'lighting.bugRated': ['lighting.bugRated'],
  'lighting.motionOrCurfewControls': ['lighting.motionOrCurfewControls'],
  'lighting.iesPhotometricAvailable': ['lighting.iesPhotometricAvailable'],
};

const DOCUMENT_FIELD_MAP:
Partial<Record<GenerationScreeningEvidenceField, GenerationScreeningPrefillField>> = {
  availableGasCapacityDthPerDay: 'availableGasCapacityDthPerDay',
  availableWaterConsumptionGalPerDay: 'availableWaterConsumptionGalPerDay',
  ccsExportCapacityTonnesPerYear: 'ccsExportCapacityTonnesPerYear',
  'water.dataCenterConsumptionGalPerMWhFacility':
    'water.dataCenterConsumptionGalPerMWhFacility',
  'receptors.distanceM': 'receptors.nearest.distanceM',
  'lighting.fixtureCount': 'lighting.fixtureCount',
  'lighting.wattsPerFixture': 'lighting.wattsPerFixture',
  'lighting.lumensPerFixture': 'lighting.lumensPerFixture',
  'lighting.fullyShielded': 'lighting.fullyShielded',
  'lighting.bugRated': 'lighting.bugRated',
  'lighting.motionOrCurfewControls': 'lighting.motionOrCurfewControls',
  'lighting.iesPhotometricAvailable': 'lighting.iesPhotometricAvailable',
};

const EXPECTED_DOCUMENT_UNITS:
Partial<Record<GenerationScreeningEvidenceField, string>> = {
  availableGasCapacityDthPerDay: 'Dth/day',
  availableWaterConsumptionGalPerDay: 'gal/day',
  ccsExportCapacityTonnesPerYear: 'metric-tonne/year',
  'water.dataCenterConsumptionGalPerMWhFacility': 'gal/MWh-facility',
  'receptors.distanceM': 'm',
  'lighting.fixtureCount': 'count',
  'lighting.wattsPerFixture': 'W',
  'lighting.lumensPerFixture': 'lm',
  'lighting.fullyShielded': 'boolean',
  'lighting.bugRated': 'boolean',
  'lighting.motionOrCurfewControls': 'boolean',
  'lighting.iesPhotometricAvailable': 'boolean',
};

const LIGHTING_BOOLEAN_FIELDS = new Set<GenerationScreeningEvidenceField>([
  'lighting.fullyShielded',
  'lighting.bugRated',
  'lighting.motionOrCurfewControls',
  'lighting.iesPhotometricAvailable',
]);

function finiteNonNegative(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0;
}

function confidenceBand(confidence: number | undefined): 'low' | 'medium' | 'high' {
  if (confidence != null && confidence >= 0.85) return 'high';
  if (confidence != null && confidence >= 0.65) return 'medium';
  return 'low';
}

function sameValue(left: PrefillValue, right: PrefillValue): boolean {
  if (typeof left !== typeof right) return false;
  if (typeof left === 'boolean') return left === right;
  const scale = Math.max(1, Math.abs(left), Math.abs(right as number));
  return Math.abs(left - (right as number)) <= scale * 1e-9;
}

function normalizeText(value: string): string {
  return value.replace(/\s+/g, ' ').trim().toLowerCase();
}

function uniqueEvidenceRefs(
  refs: readonly GenerationAssumptionEvidenceRef[],
): GenerationAssumptionEvidenceRef[] {
  const keyed = new Map<string, GenerationAssumptionEvidenceRef>();
  for (const ref of refs) keyed.set(JSON.stringify(ref), ref);
  return [...keyed.values()];
}

function documentLineageIsValid(
  document: SiteDocument,
  fact: GenerationScreeningDocumentFact,
): boolean {
  const excerpt = fact.provenance.excerpt.trim();
  if (excerpt.length === 0) return false;

  const offset = fact.provenance.sourceOffset;
  const body = document.fullExtractedText ?? document.textContent;
  if (offset) {
    if (!Number.isInteger(offset.start)
      || !Number.isInteger(offset.end)
      || offset.start < 0
      || offset.end <= offset.start
      || offset.end > body.length) return false;
    const passage = normalizeText(body.slice(offset.start, offset.end));
    const normalizedExcerpt = normalizeText(excerpt);
    if (!passage.includes(normalizedExcerpt)
      && !normalizedExcerpt.includes(passage)) return false;
  }

  if (fact.provenance.page != null && document.pages?.length) {
    if (!document.pages.some(page => page.page === fact.provenance.page)) return false;
  }
  return true;
}

export function explicitFirmSiteDeliveryBasis(basis: string | undefined): boolean {
  if (!basis) return false;
  const normalized = normalizeText(basis);
  const firm = /\b(firm|contracted|reserved|committed)\b/.test(normalized);
  const siteDelivery =
    /\bsite[- ]deliverable\b/.test(normalized)
    || /\bsite delivery\b/.test(normalized)
    || /\bdelivery point\b/.test(normalized)
    || /\bmeter(?:ed)? capacity\b/.test(normalized)
    || /\bsite interconnect\b/.test(normalized);
  return firm && siteDelivery;
}

/**
 * Exact energy-rate conversions permitted for a firm, site-deliverable gas
 * fact. Volumetric rates (MMSCFD/MMscfd) are deliberately unsupported because
 * they require a documented HHV/gas-quality basis.
 */
export function convertFirmGasCapacityToDthPerDay(
  value: number,
  unit: string,
  opts?: {
    /**
     * Documented gas higher-heating value (Btu/scf) — unlocks STANDARD-VOLUME
     * units. MMscf/day × HHV [Btu/scf] = Dth/day EXACTLY (1 MMscf = 1e6 scf,
     * 1 Dth = 1e6 Btu — the megas cancel; same identity gasInterconnectDerate
     * documents). Callers pass the SED's `fuelSupply.qualitySpecs.hhvMinBtuScf`
     * (the MIN bound — conservative) with a disclosure citing the doc.
     * Absent → volumetric units still REFUSE (undocumented HHV is not 1000).
     */
    documentedHhvBtuPerScf?: number;
  },
): number | null {
  if (!finiteNonNegative(value)) return null;
  const normalized = unit
    .trim()
    .toLowerCase()
    .replaceAll(' ', '')
    .replace('mmbtu', 'mmbtu')
    .replace('dekatherm', 'dth');
  if (['dth/day', 'dth/d', 'mmbtu/day', 'mmbtu/d'].includes(normalized)) {
    return value;
  }
  if (['dth/hour', 'dth/hr', 'dth/h', 'mmbtu/hour', 'mmbtu/hr', 'mmbtu/h']
    .includes(normalized)) {
    return value * 24;
  }
  if (['mmscfd', 'mmscf/day', 'mmscf/d'].includes(normalized)) {
    const hhv = opts?.documentedHhvBtuPerScf;
    if (typeof hhv === 'number' && Number.isFinite(hhv) && hhv > 0) {
      return value * hhv;
    }
    return null; // volumetric without a documented HHV stays a refusal
  }
  return null;
}

export function convertConsumptiveUseAfyToGalPerDay(acreFeetPerYear: number): number | null {
  if (!finiteNonNegative(acreFeetPerYear)) return null;
  return acreFeetPerYear * GALLONS_PER_ACRE_FOOT / DAYS_PER_YEAR;
}

export function convertMgdToGalPerDay(mgd: number): number | null {
  if (!finiteNonNegative(mgd)) return null;
  return mgd * GALLONS_PER_MGD;
}

export function convertLitresPerKwhItToGalPerMwhFacility(
  litresPerKwhIt: number,
  pue: number,
): number | null {
  if (!finiteNonNegative(litresPerKwhIt)
    || !Number.isFinite(pue)
    || pue < 1
    || pue > 3) return null;
  return litresPerKwhIt * GALLONS_PER_MWH_IT_PER_LITRE_PER_KWH_IT / pue;
}

function directDocumentFactIsQualified(
  fact: GenerationScreeningDocumentFact,
): boolean {
  switch (fact.field) {
    case 'availableGasCapacityDthPerDay':
    case 'ccsExportCapacityTonnesPerYear':
      return fact.qualification.firm === true
        && fact.qualification.siteDeliverable === true;
    case 'availableWaterConsumptionGalPerDay':
    case 'water.dataCenterConsumptionGalPerMWhFacility':
      return fact.qualification.consumption === true
        && fact.qualification.withdrawal !== true;
    default:
      if (fact.field !== 'unsupported' && LIGHTING_BOOLEAN_FIELDS.has(fact.field)) {
        return fact.qualification.installedDesign === true
          && fact.canonical?.value === true;
      }
      return true;
  }
}

/**
 * Deterministic re-check of a persisted (already 'eligible') water fact.
 * Facts validated before the quoted-number role rule still carry the model's
 * consumption flag alone, so re-read the role of the stored quote at the
 * stored raw value. The printings checked are the ones the extraction
 * validator binds (the raw value, or the selected design value, carrying the
 * raw unit; screeningWaterRoleTokens), so a fact eligible at extraction is
 * judged on the same tokens here. Every such printing must be a
 * consumptive-use quantity for the field; an allocation, whole-site demand,
 * margin, withdrawal/makeup, firm delivery or UNKNOWN role, or no unit-bound
 * printing at all, fails closed.
 */
function documentWaterFactRoleIsBound(fact: GenerationScreeningDocumentFact): boolean {
  if (fact.field === 'unsupported' || !isScreeningWaterFactField(fact.field)) return true;
  const excerpt = fact.provenance.excerpt;
  return screeningWaterFactRoleIsBound(fact.field, excerpt,
    screeningWaterRoleTokens(excerpt, fact.raw.value, fact.raw.unit, fact.raw.range, fact.field));
}

function factApplies(
  fact: GenerationScreeningDocumentFact,
  technology: GenerationTechnology,
  coolingSystem: string | undefined,
  loadBasis: 'facility' | 'it-load' | undefined,
): 'technology-mismatch' | 'cooling-mismatch' | 'load-basis-mismatch' | null {
  if (fact.applicability.technologies?.length
    && !fact.applicability.technologies.includes(technology)) {
    return 'technology-mismatch';
  }
  if (fact.applicability.coolingSystems?.length
    && (!coolingSystem || !fact.applicability.coolingSystems.includes(
      coolingSystem as typeof fact.applicability.coolingSystems[number],
    ))) {
    return 'cooling-mismatch';
  }
  if (fact.applicability.loadBasis && fact.applicability.loadBasis !== loadBasis) {
    return 'load-basis-mismatch';
  }
  return null;
}

function documentCandidates(
  args: ResolveGenerationScreeningPrefillArgs,
  rejections: GenerationScreeningPrefillRejection[],
): GenerationScreeningPrefillCandidate[] {
  const documents = args.documents ?? [];
  const supersededIds = new Set(
    documents.map(document => document.replacesDocId).filter(
      (id): id is string => typeof id === 'string' && id.length > 0,
    ),
  );
  const technology = args.input.units[0]?.technology ?? 'combined-cycle-block';
  const coolingSystem = args.input.equipmentBasis?.screeningNormalization.coolingSystem;
  const loadBasis = isIntentFieldExplicitlyUnknown(args.site.projectIntent, 'powerBasis')
    ? undefined
    : args.site.projectIntent?.powerBasis ?? args.sed?.power?.data?.powerBasis ?? undefined;
  const candidates: GenerationScreeningPrefillCandidate[] = [];

  for (const document of documents) {
    const extraction = document.generationScreeningExtraction;
    if (!extraction) continue;
    const excluded = args.site.evidenceMode === 'ai-only'
      || document.includeInSed === false
      || document.archived === true
      || document.deletedAt != null
      || document.needsOcr === true
      || supersededIds.has(document.id);
    if (excluded) {
      rejections.push({
        source: 'document',
        sourceId: document.id,
        code: 'document-excluded',
        message: `${document.fileName} is archived, deleted, unreadable, superseded, or excluded from evidence use.`,
      });
      continue;
    }
    if (!document.contentHash) {
      rejections.push({
        source: 'document',
        sourceId: document.id,
        code: 'hash-missing',
        message: `${document.fileName} has no SHA-256 and remains review-only; re-upload it to use values automatically.`,
      });
      continue;
    }
    if (extraction.documentSha256 !== document.contentHash) {
      rejections.push({
        source: 'document',
        sourceId: document.id,
        code: 'hash-stale',
        message: `${document.fileName} changed after screening extraction; re-extract it before applying evidence.`,
      });
      continue;
    }
    if (extraction.extractorVersion !== GENERATION_SCREENING_EXTRACTOR_VERSION) {
      rejections.push({
        source: 'document',
        sourceId: document.id,
        code: 'extractor-stale',
        message: `${document.fileName} was screened by ${extraction.extractorVersion}; run the current extractor before applying it.`,
      });
      continue;
    }
    if (extraction.status !== 'complete') {
      rejections.push({
        source: 'document',
        sourceId: document.id,
        code: 'extraction-incomplete',
        message: `${document.fileName} has no complete, eligible Generation screening extraction.`,
      });
      continue;
    }

    for (const fact of extraction.facts) {
      if (fact.field === 'unsupported') continue;
      const mappedField = DOCUMENT_FIELD_MAP[fact.field];
      if (!mappedField || fact.target !== 'screening-input') continue;
      if (fact.status !== 'eligible' || !fact.canonical) {
        rejections.push({
          source: 'document',
          sourceId: `${document.id}:${fact.id}`,
          field: mappedField,
          code: 'fact-not-eligible',
          message: `${document.fileName}: ${fact.field} is review-only or was rejected by deterministic validation.`,
        });
        continue;
      }
      if (fact.provenance.documentSha256 !== document.contentHash) {
        rejections.push({
          source: 'document',
          sourceId: `${document.id}:${fact.id}`,
          field: mappedField,
          code: 'hash-stale',
          message: `${document.fileName}: fact lineage does not match the current document hash.`,
        });
        continue;
      }
      if (fact.canonical.unit !== EXPECTED_DOCUMENT_UNITS[fact.field]) {
        rejections.push({
          source: 'document',
          sourceId: `${document.id}:${fact.id}`,
          field: mappedField,
          code: 'unit-mismatch',
          message: `${document.fileName}: ${fact.canonical.unit} is not the canonical unit for ${fact.field}.`,
        });
        continue;
      }
      const value = fact.canonical.value;
      if ((typeof value === 'number' && !finiteNonNegative(value))
        || (typeof value !== 'number' && typeof value !== 'boolean')) {
        rejections.push({
          source: 'document',
          sourceId: `${document.id}:${fact.id}`,
          field: mappedField,
          code: 'invalid-value',
          message: `${document.fileName}: ${fact.field} is non-finite, negative, or has the wrong value type.`,
        });
        continue;
      }
      if (fact.field === 'receptors.distanceM'
        && (typeof value !== 'number' || value <= 0)) {
        rejections.push({
          source: 'document',
          sourceId: `${document.id}:${fact.id}`,
          field: mappedField,
          code: 'invalid-value',
          message: `${document.fileName}: receptor distance must be greater than zero.`,
        });
        continue;
      }
      if (fact.field === 'lighting.fixtureCount'
        && (typeof value !== 'number' || !Number.isInteger(value))) {
        rejections.push({
          source: 'document',
          sourceId: `${document.id}:${fact.id}`,
          field: mappedField,
          code: 'invalid-value',
          message: `${document.fileName}: fixture count must be an exact non-negative integer.`,
        });
        continue;
      }
      if (!directDocumentFactIsQualified(fact)) {
        rejections.push({
          source: 'document',
          sourceId: `${document.id}:${fact.id}`,
          field: mappedField,
          code: 'basis-ambiguous',
          message: `${document.fileName}: ${fact.field} lacks firm/site, consumptive-use, or affirmative installed-design qualification.`,
        });
        continue;
      }
      if (!documentWaterFactRoleIsBound(fact)) {
        rejections.push({
          source: 'document',
          sourceId: `${document.id}:${fact.id}`,
          field: mappedField,
          code: 'basis-ambiguous',
          message: `${document.fileName}: the quoted ${fact.field} figure is not stated as a consumptive-use quantity; a site allocation, whole-site demand, margin, withdrawal/makeup or firm delivery is not consumptive use.`,
        });
        continue;
      }
      const mismatch = factApplies(fact, technology, coolingSystem, loadBasis);
      if (mismatch) {
        rejections.push({
          source: 'document',
          sourceId: `${document.id}:${fact.id}`,
          field: mappedField,
          code: mismatch,
          message: `${document.fileName}: ${fact.field} does not apply to the current technology, cooling system, or load basis.`,
        });
        continue;
      }
      if (!documentLineageIsValid(document, fact)) {
        rejections.push({
          source: 'document',
          sourceId: `${document.id}:${fact.id}`,
          field: mappedField,
          code: 'lineage-invalid',
          message: `${document.fileName}: the cited excerpt/page/offset cannot be verified against the stored text.`,
        });
        continue;
      }

      candidates.push({
        field: mappedField,
        value,
        source: 'document',
        label: `${document.fileName} · ${fact.field}`,
        citation: [
          document.fileName,
          fact.provenance.page ? `p. ${fact.provenance.page}` : null,
          fact.provenance.excerpt,
        ].filter(Boolean).join(' · '),
        confidence: confidenceBand(fact.confidence),
        evidenceRefs: [{
          kind: 'document',
          documentId: document.id,
          documentSha256: document.contentHash,
          extractionVersion: extraction.extractorVersion,
          factId: fact.id,
          ...(fact.provenance.page != null ? { page: fact.provenance.page } : {}),
          ...(fact.provenance.excerpt ? { excerpt: fact.provenance.excerpt } : {}),
          ...(fact.provenance.sourceOffset
            ? { sourceOffset: fact.provenance.sourceOffset }
            : {}),
        }],
        sourceKey: `document:${document.id}:${fact.id}`,
      });
    }
  }
  return candidates;
}

function sedSectionEligible(
  section: {
    status?: string;
    qualification?: string;
    anomalies?: readonly string[];
  } | null | undefined,
): boolean {
  if (!section) return false;
  if (!['verified', 'verified-from-doc', 'verified-from-kmz'].includes(
    section.status ?? '',
  )) return false;
  if (section.qualification
    && section.qualification !== 'verified') return false;
  return !(section.anomalies?.length);
}

function sedEvidenceRef(
  sed: SiteEvidenceDocument,
  section: string,
  fieldPath: string,
  sourceId?: string,
): GenerationAssumptionEvidenceRef {
  return {
    kind: 'sed',
    sedId: sed.id,
    section,
    fieldPath,
    ...(sourceId ? { sourceId } : {}),
    sourceVersion: String(sed.version),
  };
}

function sedCandidates(
  args: ResolveGenerationScreeningPrefillArgs,
  rejections: GenerationScreeningPrefillRejection[],
): GenerationScreeningPrefillCandidate[] {
  const { sed, site, input } = args;
  const candidates: GenerationScreeningPrefillCandidate[] = [];
  if (sed) {
    const technology = input.units[0]?.technology ?? 'combined-cycle-block';
    if (sedSectionEligible(sed.climate)) {
      const suggestion = suggestAmbientFactorFromSed(
        sed,
        technology,
        planningAmbientFactor(technology),
      );
      if (suggestion && finiteNonNegative(suggestion.value)) {
        candidates.push({
          field: 'units.ambientFactor',
          value: suggestion.value,
          source: 'site-evidence',
          label: suggestion.label,
          citation: suggestion.citation,
          confidence: suggestion.confidence,
          evidenceRefs: [sedEvidenceRef(
            sed,
            'climate',
            'climate.data.peakTemp',
            sed.climate.source,
          )],
          sourceKey: `sed:${sed.id}:climate.data.peakTemp`,
        });
      }
    } else if (sed.climate?.data?.peakTemp != null) {
      rejections.push({
        source: 'site-evidence',
        sourceId: `${sed.id}:climate`,
        field: 'units.ambientFactor',
        code: 'sed-section-ineligible',
        message: 'SED climate peak temperature is unavailable, anomalous, or not verified.',
      });
    }

    const water = sed.waterResource;
    const consumptiveUseAfy = water?.data?.consumptiveUseAfy;
    if (consumptiveUseAfy != null) {
      const providerOrValidatedDoc = water?.status === 'verified-from-doc'
        || water?.data?.waterResourceBasis === 'provider';
      const converted = convertConsumptiveUseAfyToGalPerDay(consumptiveUseAfy);
      if (sedSectionEligible(water) && providerOrValidatedDoc && converted != null) {
        candidates.push({
          field: 'availableWaterConsumptionGalPerDay',
          value: converted,
          source: 'site-evidence',
          label: `SED consumptive-use entitlement · ${consumptiveUseAfy.toLocaleString('en-US')} acre-ft/year`,
          citation: `${water?.source ?? 'SED water resource'} · 325,851 gal/acre-ft ÷ 365 days/year`,
          confidence: confidenceBand(water?.adjustedConfidence ?? water?.confidence),
          evidenceRefs: [sedEvidenceRef(
            sed,
            'waterResource',
            'waterResource.data.consumptiveUseAfy',
            water?.source,
          )],
          sourceKey: `sed:${sed.id}:waterResource.data.consumptiveUseAfy`,
        });
      } else {
        rejections.push({
          source: 'site-evidence',
          sourceId: `${sed.id}:waterResource`,
          field: 'availableWaterConsumptionGalPerDay',
          code: converted == null ? 'invalid-value' : 'sed-section-ineligible',
          message: 'SED consumptive-use AFY is invalid or is not verified provider/document evidence.',
        });
      }
    }

    const infrastructure = sed.onSiteInfrastructure;
    const flow = infrastructure?.data?.nearestPipelineMassFlow;
    const flowValue = flow?.value
      ?? infrastructure?.data?.nearestPipelineMassFlowValue;
    const flowUnit = flow?.unit
      ?? infrastructure?.data?.nearestPipelineMassFlowUnit;
    const flowBasis = flow?.basis
      ?? infrastructure?.data?.nearestPipelineMassFlowBasis;
    if (flowValue != null || flowUnit != null || flowBasis != null) {
      if (!sedSectionEligible(infrastructure)) {
        rejections.push({
          source: 'site-evidence',
          sourceId: `${sed.id}:onSiteInfrastructure`,
          field: 'availableGasCapacityDthPerDay',
          code: 'sed-section-ineligible',
          message: 'SED pipeline capacity is not from an eligible verified site-infrastructure section.',
        });
      } else if (!utilityPipelineProjectionIsCurrent(sed) || !explicitFirmSiteDeliveryBasis(flowBasis)) {
        rejections.push({
          source: 'site-evidence',
          sourceId: `${sed.id}:onSiteInfrastructure`,
          field: 'availableGasCapacityDthPerDay',
          code: 'not-firm-site-deliverable',
          message: 'Pipeline proximity or throughput is not firm, site-deliverable gas capacity.',
        });
      } else {
        const converted = typeof flowValue === 'number' && flowUnit
          ? convertFirmGasCapacityToDthPerDay(flowValue, flowUnit)
          : null;
        if (converted == null) {
          rejections.push({
            source: 'site-evidence',
            sourceId: `${sed.id}:onSiteInfrastructure`,
            field: 'availableGasCapacityDthPerDay',
            code: 'unsupported-pipeline-unit',
            message: 'Firm pipeline capacity must be in Dth/MMBtu per day or hour; MMSCFD requires an explicit HHV conversion and is not auto-applied.',
          });
        } else {
          candidates.push({
            field: 'availableGasCapacityDthPerDay',
            value: converted,
            source: 'site-evidence',
            label: 'SED firm site-deliverable gas capacity',
            citation: `${infrastructure?.source ?? 'SED on-site infrastructure'} · ${flowValue} ${flowUnit} · ${flowBasis}`,
            confidence: confidenceBand(
              infrastructure?.adjustedConfidence ?? infrastructure?.confidence,
            ),
            evidenceRefs: [sedEvidenceRef(
              sed,
              'onSiteInfrastructure',
              'onSiteInfrastructure.data.nearestPipelineMassFlow',
              infrastructure?.source,
            )],
            sourceKey: `sed:${sed.id}:onSiteInfrastructure.data.nearestPipelineMassFlow`,
          });
        }
      }
    }
  }

  const receptor = suggestNearestReceptorDistance(
    site,
    input.layoutAnchor ?? site.siteCentroid ?? site.location,
  );
  if (receptor && finiteNonNegative(receptor.value) && receptor.value > 0) {
    candidates.push({
      field: 'receptors.nearest.distanceM',
      value: receptor.value,
      source: 'site-evidence',
      label: receptor.label,
      citation: receptor.citation,
      confidence: receptor.confidence,
      evidenceRefs: [{
        kind: 'sed',
        ...(sed?.id ? { sedId: sed.id } : {}),
        section: 'siteLayers',
        fieldPath: 'siteLayers[category=impact.receptor]',
        sourceId: receptor.citation,
        ...(sed?.version != null ? { sourceVersion: String(sed.version) } : {}),
      }],
      sourceKey: `site:${site.id}:nearest-impact-receptor`,
    });
  }
  return candidates;
}

function normalizedUnit(unit: string): string {
  return unit
    .trim()
    .toLowerCase()
    .replaceAll('−', '-')
    .replaceAll('–', '-')
    .replaceAll(' ', '')
    .replaceAll('gallons', 'gal')
    .replaceAll('gallon', 'gal')
    .replaceAll('litres', 'l')
    .replaceAll('liters', 'l')
    .replaceAll('litre', 'l')
    .replaceAll('liter', 'l');
}

function benchmarkRangeValid(benchmark: NormalizedBenchmark): boolean {
  const { low, typical, high } = benchmark.value;
  return finiteNonNegative(low)
    && finiteNonNegative(typical)
    && finiteNonNegative(high)
    && low <= typical
    && typical <= high
    && benchmark.metric.trim().length > 0
    && benchmark.source.trim().length > 0
    && Number.isInteger(benchmark.vintage)
    && benchmark.vintage >= 1900
    && benchmark.vintage <= new Date().getUTCFullYear() + 1;
}

function benchmarkCitationResolves(
  dam: DatacrateModel,
  benchmark: NormalizedBenchmark,
): boolean {
  return benchmark.citationRef.trim().length > 0
    && dam.references.some(reference => reference.field === benchmark.citationRef);
}

function latestDamByType(
  dams: readonly DatacrateModel[],
  reportType: 'water-resource' | 'power-availability',
): DatacrateModel | undefined {
  return dams
    .filter(dam => dam.reportType === reportType)
    .sort((left, right) => right.version - left.version)[0];
}

function wetRecirculatingConsumptionMetric(metric: string): boolean {
  const normalized = normalizeText(metric);
  return /\bwet\b/.test(normalized)
    && /(recirculat|closed[- ]loop|cooling tower|wet tower)/.test(normalized)
    && /consum/.test(normalized)
    && !/(withdraw|data[- ]?cent)/.test(normalized);
}

function dataCenterConsumptionMetric(metric: string): boolean {
  const normalized = normalizeText(metric);
  return /data[- ]?cent/.test(normalized)
    && /water/.test(normalized)
    && /consum/.test(normalized)
    && !/withdraw/.test(normalized);
}

function solarLandMetric(metric: string): boolean {
  const normalized = normalizeText(metric);
  return /(solar|photovoltaic|\bpv\b)/.test(normalized)
    && /(land|acre|footprint)/.test(normalized);
}

function explicitFacilityPue(
  site: SiteEntity,
  sed: SiteEvidenceDocument | null | undefined,
): number | null {
  const intent = site.projectIntent;
  if (intent?.powerBasis === 'it-load') return null;
  if (intent?.powerBasis === 'facility'
    && typeof intent.pueValue === 'number'
    && Number.isFinite(intent.pueValue)
    && intent.pueValue >= 1
    && intent.pueValue <= 3) return intent.pueValue;

  const power = sed?.power;
  if (power?.status === 'verified-from-intent'
    && power.data?.powerBasis === 'facility'
    && power.data.basisProvenance === 'declared'
    && typeof power.data.pueValue === 'number'
    && Number.isFinite(power.data.pueValue)
    && power.data.pueValue >= 1
    && power.data.pueValue <= 3) return power.data.pueValue;

  const cooling = sed?.cooling;
  if (power?.data?.powerBasis === 'facility'
    && cooling?.status === 'verified-from-intent'
    && cooling.data?.pueBasis === 'declared'
    && typeof cooling.data.resolvedPue === 'number'
    && Number.isFinite(cooling.data.resolvedPue)
    && cooling.data.resolvedPue >= 1
    && cooling.data.resolvedPue <= 3) return cooling.data.resolvedPue;
  return null;
}

export function isQuarantinedFeasibilityDataCenterWaterSlot(
  dam: DatacrateModel,
): boolean {
  return dam.reportType === 'feasibility'
    && (dam.slotEntries ?? []).some(slot =>
      slot.sectionId === 'benchmarks'
      && slot.fieldKey === 'dataCenterWaterDraw');
}

function damCandidate(
  dam: DatacrateModel,
  benchmark: NormalizedBenchmark,
  field: GenerationScreeningPrefillField,
  value: number,
  basis: string,
  conversionFormula?: string,
): GenerationScreeningPrefillCandidate {
  return {
    field,
    value,
    source: 'dam',
    label: `DAM ${dam.reportType} v${dam.version} · ${benchmark.metric}`,
    citation: `${benchmark.source} · ${benchmark.value.low}–${benchmark.value.high} ${benchmark.value.unit} · ${benchmark.vintage}`,
    confidence: 'medium',
    evidenceRefs: [{
      kind: 'dam',
      reportType: dam.reportType,
      reportVersion: String(dam.version),
      benchmarkId: benchmark.citationRef,
      basis,
      vintage: String(benchmark.vintage),
      ...(conversionFormula ? { conversionFormula } : {}),
    }],
    sourceKey: `dam:${dam.reportType}:${dam.version}:${benchmark.citationRef}`,
  };
}

function damCandidates(
  args: ResolveGenerationScreeningPrefillArgs,
  rejections: GenerationScreeningPrefillRejection[],
): GenerationScreeningPrefillCandidate[] {
  const dams = args.dams ?? [];
  const candidates: GenerationScreeningPrefillCandidate[] = [];
  for (const dam of dams) {
    if (isQuarantinedFeasibilityDataCenterWaterSlot(dam)) {
      // P3.7 (2026-09-05) — the feasibility `dataCenterWaterDraw` slot stores
      // L/kWh-IT (2.15) under a legacy contract that declared gal/MWh: a ~123×
      // unit defect. It was quarantined (routed around) rather than corrected,
      // so any DAM authored under the old contract carried the same latent
      // mismatch. Convert at this boundary with the same named constant and
      // PUE rule the water-DAM benchmarks use; without a validated facility
      // PUE the slot is still refused, now with the reason that applies.
      const slot = (dam.slotEntries ?? []).find(entry =>
        entry.sectionId === 'benchmarks' && entry.fieldKey === 'dataCenterWaterDraw')!;
      const litresPerKwhIt = Number(slot.value);
      const sourceId = `dam:${dam.reportType}:${dam.version}:benchmarks.dataCenterWaterDraw`;
      const pue = explicitFacilityPue(args.site, args.sed);
      if (pue == null) {
        rejections.push({
          source: 'dam',
          sourceId,
          field: 'water.dataCenterConsumptionGalPerMWhFacility',
          code: 'pue-required',
          message: 'The feasibility dataCenterWaterDraw slot is stated in L/kWh-IT; converting it to a facility-energy basis requires a declared facility load basis and validated PUE.',
        });
      } else {
        const converted = convertLitresPerKwhItToGalPerMwhFacility(litresPerKwhIt, pue);
        if (converted == null) {
          rejections.push({
            source: 'dam',
            sourceId,
            field: 'water.dataCenterConsumptionGalPerMWhFacility',
            code: 'unit-mismatch',
            message: `The feasibility dataCenterWaterDraw slot value ${String(slot.value)} is not a finite L/kWh-IT quantity.`,
          });
        } else {
          candidates.push({
            field: 'water.dataCenterConsumptionGalPerMWhFacility',
            value: converted,
            source: 'dam',
            label: `DAM ${dam.reportType} v${dam.version} · benchmarks.dataCenterWaterDraw`,
            citation: `${slot.rationale?.trim() || 'feasibility DAM slot'} · ${litresPerKwhIt} L/kWh-IT`,
            confidence: 'low',
            evidenceRefs: [{
              kind: 'dam',
              reportType: dam.reportType,
              reportVersion: String(dam.version),
              benchmarkId: slot.id,
              basis: `legacy feasibility slot stated in L/kWh-IT; facility basis at PUE ${pue}`,
              vintage: String(dam.version),
              conversionFormula: `${litresPerKwhIt} L/kWh-IT × ${GALLONS_PER_MWH_IT_PER_LITRE_PER_KWH_IT} gal/MWh-IT ÷ PUE ${pue}`,
            }],
            sourceKey: sourceId,
          });
        }
      }
    }
  }

  const waterDam = latestDamByType(dams, 'water-resource');
  if (waterDam) {
    const coolingBasis = args.input.equipmentBasis
      ?.screeningNormalization.coolingSystem
      ?? (args.site.projectIntent?.bopHeatRejection === 'wet-cooling-tower'
        ? 'closed-loop-wet'
        : undefined);
    const wetBenchmarks = waterDam.benchmarks.filter(benchmark =>
      wetRecirculatingConsumptionMetric(benchmark.metric));
    for (const benchmark of wetBenchmarks) {
      if (!benchmarkRangeValid(benchmark)) {
        rejections.push({
          source: 'dam',
          sourceId: `dam:${waterDam.version}:${benchmark.citationRef}`,
          field: 'water.generationConsumptionGalPerMWhGross',
          code: 'benchmark-invalid',
          message: `${benchmark.metric} has a non-finite, negative, unordered, or stale range.`,
        });
        continue;
      }
      if (!benchmarkCitationResolves(waterDam, benchmark)) {
        rejections.push({
          source: 'dam',
          sourceId: `dam:${waterDam.version}:${benchmark.citationRef}`,
          field: 'water.generationConsumptionGalPerMWhGross',
          code: 'benchmark-citation-missing',
          message: `${benchmark.metric} does not resolve to a DAM reference.`,
        });
        continue;
      }
      if (coolingBasis !== 'closed-loop-wet') {
        rejections.push({
          source: 'dam',
          sourceId: `dam:${waterDam.version}:${benchmark.citationRef}`,
          field: 'water.generationConsumptionGalPerMWhGross',
          code: 'benchmark-not-applicable',
          message: 'Wet-recirculating generation consumption requires an explicit closed-loop-wet project/equipment cooling basis.',
        });
        continue;
      }
      if (!['gal/mwh', 'gal/mwh-gross'].includes(normalizedUnit(benchmark.value.unit))) {
        rejections.push({
          source: 'dam',
          sourceId: `dam:${waterDam.version}:${benchmark.citationRef}`,
          field: 'water.generationConsumptionGalPerMWhGross',
          code: 'unit-mismatch',
          message: `${benchmark.metric} must be expressed in gal/MWh gross.`,
        });
        continue;
      }
      candidates.push(damCandidate(
        waterDam,
        benchmark,
        'water.generationConsumptionGalPerMWhGross',
        benchmark.value.typical,
        'wet-recirculating generation consumption',
      ));
    }

    const dcBenchmarks = waterDam.benchmarks.filter(benchmark =>
      dataCenterConsumptionMetric(benchmark.metric));
    for (const benchmark of dcBenchmarks) {
      if (!benchmarkRangeValid(benchmark)) {
        rejections.push({
          source: 'dam',
          sourceId: `dam:${waterDam.version}:${benchmark.citationRef}`,
          field: 'water.dataCenterConsumptionGalPerMWhFacility',
          code: 'benchmark-invalid',
          message: `${benchmark.metric} has a non-finite, negative, unordered, or stale range.`,
        });
        continue;
      }
      if (!benchmarkCitationResolves(waterDam, benchmark)) {
        rejections.push({
          source: 'dam',
          sourceId: `dam:${waterDam.version}:${benchmark.citationRef}`,
          field: 'water.dataCenterConsumptionGalPerMWhFacility',
          code: 'benchmark-citation-missing',
          message: `${benchmark.metric} does not resolve to a DAM reference.`,
        });
        continue;
      }
      const pue = explicitFacilityPue(args.site, args.sed);
      if (pue == null) {
        rejections.push({
          source: 'dam',
          sourceId: `dam:${waterDam.version}:${benchmark.citationRef}`,
          field: 'water.dataCenterConsumptionGalPerMWhFacility',
          code: 'pue-required',
          message: 'Data-center IT-basis water consumption requires a declared facility load basis and validated PUE.',
        });
        continue;
      }
      const unit = normalizedUnit(benchmark.value.unit);
      const metric = normalizeText(benchmark.metric);
      let converted: number | null = null;
      let formula: string | undefined;
      if (['l/kwh-it', 'l/kwhit', 'l/kwh'].includes(unit)
        && /\bit\b/.test(`${metric} ${normalizeText(benchmark.value.unit)}`)) {
        converted = convertLitresPerKwhItToGalPerMwhFacility(
          benchmark.value.typical,
          pue,
        );
        formula = `typical L/kWh-IT × ${GALLONS_PER_MWH_IT_PER_LITRE_PER_KWH_IT} gal/MWh-IT ÷ PUE ${pue}`;
      } else if (['gal/mwh-it', 'gal/mwhit'].includes(unit)) {
        converted = benchmark.value.typical / pue;
        formula = `typical gal/MWh-IT ÷ PUE ${pue}`;
      } else if (['gal/mwh-facility', 'gal/mwhfacility'].includes(unit)) {
        converted = benchmark.value.typical;
        formula = 'DAM benchmark explicitly stated on a facility-energy basis';
      }
      if (converted == null || !finiteNonNegative(converted)) {
        rejections.push({
          source: 'dam',
          sourceId: `dam:${waterDam.version}:${benchmark.citationRef}`,
          field: 'water.dataCenterConsumptionGalPerMWhFacility',
          code: 'unit-mismatch',
          message: `${benchmark.metric} must state an exact IT or facility energy basis.`,
        });
        continue;
      }
      candidates.push(damCandidate(
        waterDam,
        benchmark,
        'water.dataCenterConsumptionGalPerMWhFacility',
        converted,
        `data-center consumption; facility basis at PUE ${pue}`,
        formula,
      ));
    }
  }

  const powerDam = latestDamByType(dams, 'power-availability');
  if (powerDam && args.input.units[0]?.technology === 'solar-bess') {
    const solarBenchmarks = powerDam.benchmarks.filter(benchmark =>
      solarLandMetric(benchmark.metric));
    for (const benchmark of solarBenchmarks) {
      if (!benchmarkRangeValid(benchmark)) {
        rejections.push({
          source: 'dam',
          sourceId: `dam:${powerDam.version}:${benchmark.citationRef}`,
          field: 'footprint.plantAcresPerGrossMW',
          code: 'benchmark-invalid',
          message: `${benchmark.metric} has a non-finite, negative, unordered, or stale range.`,
        });
        continue;
      }
      if (!benchmarkCitationResolves(powerDam, benchmark)) {
        rejections.push({
          source: 'dam',
          sourceId: `dam:${powerDam.version}:${benchmark.citationRef}`,
          field: 'footprint.plantAcresPerGrossMW',
          code: 'benchmark-citation-missing',
          message: `${benchmark.metric} does not resolve to a DAM reference.`,
        });
        continue;
      }
      if (!['acre/mw', 'acres/mw'].includes(normalizedUnit(benchmark.value.unit))) {
        rejections.push({
          source: 'dam',
          sourceId: `dam:${powerDam.version}:${benchmark.citationRef}`,
          field: 'footprint.plantAcresPerGrossMW',
          code: 'unit-mismatch',
          message: `${benchmark.metric} must be expressed in acres/MW.`,
        });
        continue;
      }
      candidates.push(damCandidate(
        powerDam,
        benchmark,
        'footprint.plantAcresPerGrossMW',
        benchmark.value.typical,
        'solar PV installed land intensity',
      ));
    }
  }
  return candidates;
}

function mergeAgreeingCandidates(
  candidates: readonly GenerationScreeningPrefillCandidate[],
): GenerationScreeningPrefillCandidate {
  const sorted = [...candidates].sort((left, right) =>
    left.sourceKey.localeCompare(right.sourceKey));
  const strongest = [...sorted].sort((left, right) => {
    const confidenceRank = { low: 0, medium: 1, high: 2 };
    return confidenceRank[right.confidence] - confidenceRank[left.confidence]
      || left.sourceKey.localeCompare(right.sourceKey);
  })[0]!;
  return {
    ...strongest,
    label: sorted.map(candidate => candidate.label).join(' + '),
    citation: sorted.map(candidate => candidate.citation).join(' | '),
    evidenceRefs: uniqueEvidenceRefs(sorted.flatMap(candidate => candidate.evidenceRefs)),
    sourceKey: sorted.map(candidate => candidate.sourceKey).join('+'),
  };
}

function resolveCandidates(
  candidates: readonly GenerationScreeningPrefillCandidate[],
): {
  winners: Map<GenerationScreeningPrefillField, GenerationScreeningPrefillCandidate>;
  conflicts: GenerationScreeningPrefillConflict[];
} {
  const winners = new Map<
    GenerationScreeningPrefillField,
    GenerationScreeningPrefillCandidate
  >();
  const conflicts: GenerationScreeningPrefillConflict[] = [];
  for (const field of GENERATION_SCREENING_PREFILL_FIELDS) {
    const forField = candidates.filter(candidate => candidate.field === field);
    if (forField.length === 0) continue;
    const highestRank = Math.max(...forField.map(candidate => SOURCE_RANK[candidate.source]));
    const highestTier = forField.filter(candidate =>
      SOURCE_RANK[candidate.source] === highestRank);
    const reference = highestTier[0]!.value;
    if (highestTier.some(candidate => !sameValue(candidate.value, reference))) {
      const sorted = [...highestTier].sort((left, right) =>
        left.sourceKey.localeCompare(right.sourceKey));
      conflicts.push({
        field,
        source: sorted[0]!.source,
        message: `Conflicting ${sorted[0]!.source} values for ${field}; no lower-tier fallback was applied.`,
        candidates: sorted,
      });
      continue;
    }
    winners.set(field, mergeAgreeingCandidates(highestTier));
  }
  return { winners, conflicts };
}

function currentValue(
  input: GenerationImpactScenarioInput,
  field: GenerationScreeningPrefillField,
): PrefillValue | undefined {
  switch (field) {
    case 'units.ambientFactor': return input.units[0]?.ambientFactor;
    case 'availableGasCapacityDthPerDay': return input.availableGasCapacityDthPerDay;
    case 'availableWaterConsumptionGalPerDay':
      return input.availableWaterConsumptionGalPerDay;
    case 'ccsExportCapacityTonnesPerYear': return input.ccsExportCapacityTonnesPerYear;
    case 'water.generationConsumptionGalPerMWhGross':
      return input.water.generationConsumptionGalPerMWhGross;
    case 'water.dataCenterConsumptionGalPerMWhFacility':
      return input.water.dataCenterConsumptionGalPerMWhFacility;
    case 'footprint.plantAcresPerGrossMW': return input.footprint.plantAcresPerGrossMW;
    case 'receptors.nearest.distanceM': return input.receptors[0]?.distanceM;
    case 'lighting.fixtureCount': return input.lighting.fixtureCount;
    case 'lighting.wattsPerFixture': return input.lighting.wattsPerFixture;
    case 'lighting.lumensPerFixture': return input.lighting.lumensPerFixture;
    case 'lighting.fullyShielded': return input.lighting.fullyShielded;
    case 'lighting.bugRated': return input.lighting.bugRated;
    case 'lighting.motionOrCurfewControls': return input.lighting.motionOrCurfewControls;
    case 'lighting.iesPhotometricAvailable':
      return input.lighting.iesPhotometricAvailable;
  }
}

function assumptionProtectsField(
  input: GenerationImpactScenarioInput,
  field: GenerationScreeningPrefillField,
): boolean {
  const aliases = ASSUMPTION_FIELD_ALIASES[field];
  const assumption = [...input.assumptions].reverse().find(candidate =>
    aliases.includes(candidate.field));
  if (!assumption) return false;
  if (PROTECTED_ASSUMPTION_SOURCES.has(assumption.source)) return true;
  if (assumption.source === 'planning-default') return false;
  return AUTO_ASSUMPTION_SOURCES.has(assumption.source)
    && assumption.autoPopulated !== true;
}

function authorityReason(
  input: GenerationImpactScenarioInput,
  field: GenerationScreeningPrefillField,
): GenerationScreeningPrefillDecision['reason'] | null {
  const equipmentFields = new Set<GenerationScreeningPrefillField>([
    'units.ambientFactor',
    'water.generationConsumptionGalPerMWhGross',
    'footprint.plantAcresPerGrossMW',
  ]);
  if (input.equipmentBasis && equipmentFields.has(field)) {
    return 'equipment-authority';
  }
  if (input.projectDutyBasis) {
    if (field === 'units.ambientFactor' && input.projectDutyBasis.performance) {
      return 'project-duty-authority';
    }
    if (field === 'water.generationConsumptionGalPerMWhGross'
      && input.projectDutyBasis.water) return 'project-duty-authority';
    if (field === 'footprint.plantAcresPerGrossMW'
      && input.projectDutyBasis.installedReservations) {
      return 'project-duty-authority';
    }
  }
  return null;
}

function applyValue(
  input: GenerationImpactScenarioInput,
  candidate: GenerationScreeningPrefillCandidate,
): GenerationImpactScenarioInput {
  const field = candidate.field;
  const numeric = typeof candidate.value === 'number' ? candidate.value : null;
  const assumptions = [
    ...input.assumptions.filter(assumption =>
      !ASSUMPTION_FIELD_ALIASES[field].includes(assumption.field)),
    {
      field,
      value: candidate.value,
      source: candidate.source,
      label: candidate.label,
      citation: candidate.citation,
      confidence: candidate.confidence,
      evidenceRefs: candidate.evidenceRefs,
      autoPopulated: true,
    },
  ];
  switch (field) {
    case 'units.ambientFactor':
      return {
        ...input,
        units: input.units.map(unit => ({ ...unit, ambientFactor: numeric! })),
        assumptions,
      };
    case 'availableGasCapacityDthPerDay':
      return { ...input, availableGasCapacityDthPerDay: numeric!, assumptions };
    case 'availableWaterConsumptionGalPerDay':
      return { ...input, availableWaterConsumptionGalPerDay: numeric!, assumptions };
    case 'ccsExportCapacityTonnesPerYear':
      return { ...input, ccsExportCapacityTonnesPerYear: numeric!, assumptions };
    case 'water.generationConsumptionGalPerMWhGross':
      return {
        ...input,
        water: { ...input.water, generationConsumptionGalPerMWhGross: numeric! },
        assumptions,
      };
    case 'water.dataCenterConsumptionGalPerMWhFacility':
      return {
        ...input,
        water: { ...input.water, dataCenterConsumptionGalPerMWhFacility: numeric! },
        assumptions,
      };
    case 'footprint.plantAcresPerGrossMW':
      return {
        ...input,
        footprint: { ...input.footprint, plantAcresPerGrossMW: numeric! },
        assumptions,
      };
    case 'receptors.nearest.distanceM':
      return {
        ...input,
        receptors: [{
          id: input.receptors[0]?.id ?? 'nearest-residence',
          label: input.receptors[0]?.label ?? 'Nearest documented receptor',
          distanceM: numeric!,
          barrierLossDb: input.receptors[0]?.barrierLossDb ?? 0,
          thresholdDbA: input.receptors[0]?.thresholdDbA ?? 45,
          ...(input.receptors[0]?.location
            ? { location: input.receptors[0].location }
            : {}),
        }],
        assumptions,
      };
    case 'lighting.fixtureCount':
      return {
        ...input,
        lighting: { ...input.lighting, fixtureCount: numeric! },
        assumptions,
      };
    case 'lighting.wattsPerFixture':
      return {
        ...input,
        lighting: { ...input.lighting, wattsPerFixture: numeric! },
        assumptions,
      };
    case 'lighting.lumensPerFixture':
      return {
        ...input,
        lighting: { ...input.lighting, lumensPerFixture: numeric! },
        assumptions,
      };
    case 'lighting.fullyShielded':
      return {
        ...input,
        lighting: { ...input.lighting, fullyShielded: candidate.value as boolean },
        assumptions,
      };
    case 'lighting.bugRated':
      return {
        ...input,
        lighting: { ...input.lighting, bugRated: candidate.value as boolean },
        assumptions,
      };
    case 'lighting.motionOrCurfewControls':
      return {
        ...input,
        lighting: {
          ...input.lighting,
          motionOrCurfewControls: candidate.value as boolean,
        },
        assumptions,
      };
    case 'lighting.iesPhotometricAvailable':
      return {
        ...input,
        lighting: {
          ...input.lighting,
          iesPhotometricAvailable: candidate.value as boolean,
        },
        assumptions,
      };
  }
}

export function isGenerationScreeningAutoSource(
  source: AssumptionSource,
): source is GenerationScreeningAutomaticSource | 'planning-default' {
  return AUTO_ASSUMPTION_SOURCES.has(source);
}

/**
 * Resolve and apply all eligible automatic evidence to a planning draft.
 *
 * Source precedence is document > SED/site > DAM > the already-built planning
 * baseline. A same-tier conflict blocks the field and does not fall through.
 * The returned source input and every evidence source remain unmodified.
 */
export function resolveGenerationScreeningPrefill(
  args: ResolveGenerationScreeningPrefillArgs,
): GenerationScreeningPrefillResolution {
  const rejections: GenerationScreeningPrefillRejection[] = [];
  const candidates = [
    ...documentCandidates(args, rejections),
    ...sedCandidates(args, rejections),
    ...damCandidates(args, rejections),
  ];
  const { winners, conflicts } = resolveCandidates(candidates);
  const decisions: GenerationScreeningPrefillDecision[] = [];
  let next = args.input;

  for (const field of GENERATION_SCREENING_PREFILL_FIELDS) {
    const candidate = winners.get(field);
    if (!candidate) continue;
    const previousValue = currentValue(next, field);
    let reason: GenerationScreeningPrefillDecision['reason'] = 'applied';
    if (args.protectedFields?.has(field)) {
      reason = 'protected-field';
    } else if (args.refreshableFields && !args.refreshableFields.has(field)) {
      reason = 'not-refreshable';
    } else {
      const authority = authorityReason(next, field);
      if (authority) reason = authority;
      else if (assumptionProtectsField(next, field)) reason = 'protected-assumption';
      else if (previousValue !== undefined && sameValue(previousValue, candidate.value)) {
        reason = 'already-current';
      }
    }
    const applied = reason === 'applied' || reason === 'already-current';
    if (applied) next = applyValue(next, candidate);
    decisions.push({ field, candidate, previousValue, applied, reason });
  }

  return { input: next, decisions, conflicts, rejections };
}
